/** SPECS:
 *  __tests__/use-polling.test.tsx
 *  __tests__/usePolling-interval.test.tsx
 */
/**
 * Shared polling hook for Mission Control components.
 * 
 * Features:
 * - Skips fetch if previous request is still in-flight
 * - Exposes error state, isLoading, and lastUpdated
 * - Pauses when tab is hidden or offline
 * - Can be manually paused via option
 * - Refetches immediately when connectivity returns
 * - Spreads periodic refreshes after simultaneous mounts to avoid repeated bursts
 * - Cleanup on unmount
 * - Stable-reference data: skips setData when the JSON response is deep-equal
 *   to the previous value, preventing downstream render churn every poll tick.
 */
'use client';

import { useState, useEffect, useRef, useCallback } from 'react';

type JsonResponse = Record<string, any>;

/**
 * Deep-equality comparison for JSON-like values.
 * Returns false for non-plain objects (e.g. class instances) so callers that
 * pass specialized values can supply their own compare function.
 */
function jsonDeepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  const ta = typeof a;
  const tb = typeof b;
  if (ta !== tb) return false;
  if (ta !== 'object') return false;

  if (Array.isArray(a) !== Array.isArray(b)) return false;

  const aProto = Object.getPrototypeOf(a);
  const bProto = Object.getPrototypeOf(b);
  if (Array.isArray(a)) {
    if ((a as unknown[]).length !== (b as unknown[]).length) return false;
    for (let i = 0; i < (a as unknown[]).length; i++) {
      if (!jsonDeepEqual((a as unknown[])[i], (b as unknown[])[i])) return false;
    }
    return true;
  }

  // Only compare plain objects. Everything else falls back to reference equality.
  if (aProto !== Object.prototype || bProto !== Object.prototype) return false;

  const aKeys = Object.keys(a as Record<string, unknown>);
  const bKeys = Object.keys(b as Record<string, unknown>);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    if (!jsonDeepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])) return false;
  }
  return true;
}

async function parseHttpError(res: Response): Promise<string> {
  const fallback = `HTTP ${res.status}: ${res.statusText || 'Request failed'}`;
  try {
    const payload: unknown = await res.json();
    if (payload && typeof payload === 'object') {
      const errorPayload = payload as Record<string, unknown>;
      if (typeof errorPayload.error === 'string' && errorPayload.error.trim()) return errorPayload.error;
      if (typeof errorPayload.message === 'string' && errorPayload.message.trim()) return errorPayload.message;
    }
  } catch {
    // Keep the HTTP fallback when an error response is not JSON.
  }
  return fallback;
}

interface UsePollingOptions<T> {
  url: string | null;
  intervalMs: number;
  transform?: (_data: JsonResponse) => T;
  /** Optional comparator for the fetched/transformed value. Default is a JSON-safe deep equality. */
  compare?: (_a: T, _b: T) => boolean;
  initialValue: T;
  isPaused?: boolean;
  /** Per-request timeout. Aborts a fetch that hangs so the poller recovers. Default 15s. */
  timeoutMs?: number;
}

interface UsePollingResult<T> {
  data: T;
  error: string | null;
  isLoading: boolean;
  lastUpdated: Date | null;
  refresh: () => Promise<void>;
}

export function usePolling<T>({
  url,
  intervalMs,
  transform,
  compare,
  initialValue,
  isPaused = false,
  timeoutMs = 15000,
}: UsePollingOptions<T>): UsePollingResult<T> {
  const [data, setData] = useState<T>(initialValue);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const dataRef = useRef<T>(initialValue);
  const errorRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const transformRef = useRef(transform);
  transformRef.current = transform;
  const compareRef = useRef(compare ?? jsonDeepEqual);
  compareRef.current = compare ?? jsonDeepEqual;

  const setStableData = useCallback((next: T) => {
    if (!(compareRef.current as (_a: T, _b: T) => boolean)(dataRef.current, next)) {
      dataRef.current = next;
      setData(next);
    }
  }, []);

  const setStableError = useCallback((next: string | null) => {
    if (errorRef.current !== next) {
      errorRef.current = next;
      setError(next);
    }
  }, []);

  const fetchData = useCallback(async (opts?: { force?: boolean }) => {
    if (!url) return;
    if (inFlightRef.current && !opts?.force) return;

    // Don't fetch when offline
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setIsLoading(false);
      return;
    }

    // Skip background polling while the tab is hidden — the visibilitychange
    // handler fires a fresh fetch when the tab becomes visible again. A manual
    // refresh ({ force: true }) always proceeds.
    if (!opts?.force && typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      setIsLoading(false);
      return;
    }

    inFlightRef.current = true;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    // Abort a hung request so the poller can recover on the next tick.
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) {
        throw new Error(await parseHttpError(res));
      }
      const json = await res.json().catch(e => {
        console.error('Failed to parse JSON response:', e);
        throw new Error('Invalid JSON response from server');
      });
      if (controller.signal.aborted || abortRef.current !== controller) return;
      const result = transformRef.current ? transformRef.current(json) : json;
      setStableData(result);
      setStableError(null);
      setLastUpdated(new Date());
    } catch (err) {
      if (abortRef.current !== controller) return;
      if (err instanceof DOMException && err.name === 'AbortError') return;
      console.error('usePolling fetch error:', err);
      setStableError(err instanceof Error ? err.message : 'Fetch failed');
    } finally {
      clearTimeout(timeoutId);
      if (abortRef.current === controller) {
        inFlightRef.current = false;
        setIsLoading(false);
      }
    }
  }, [url, timeoutMs, setStableData, setStableError]);

  useEffect(() => {
    if (isPaused) return;

    fetchData();
    // intervalMs <= 0 means "fetch once on mount + manual refresh / visibility /
    // reconnect only" — NOT "poll as fast as possible". setInterval(fn, 0) would fire
    // continuously; with the in-flight guard that becomes a tight back-to-back loop that
    // hammers the endpoint (this took MC offline when a heavy dry-run endpoint was polled
    // this way). Only arm the interval for a positive period.
    // Keep the initial read immediate, then spread the first periodic refresh
    // across the latter three quarters of the interval. Each instance retains
    // that phase at the normal cadence, including after a synchronized reconnect.
    // The first refresh never waits longer than the caller's normal interval.
    let interval: ReturnType<typeof setInterval> | null = null;
    const phaseTimer = intervalMs > 0 ? setTimeout(() => {
      void fetchData();
      interval = setInterval(fetchData, intervalMs);
    }, intervalMs * (0.25 + 0.75 * Math.random())) : null;

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    const handleOnline = () => fetchData();
    window.addEventListener('online', handleOnline);

    return () => {
      if (phaseTimer !== null) clearTimeout(phaseTimer);
      if (interval !== null) clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('online', handleOnline);
      abortRef.current?.abort();
      // Release the cancelled request before the replacement effect starts
      // (URL changes and StrictMode remounts). Its finally block must not
      // clear a newer request's guard or suppress the new mount's first read.
      abortRef.current = null;
      inFlightRef.current = false;
    };
  }, [fetchData, intervalMs, isPaused]);

  return { 
    data, 
    error, 
    isLoading, 
    lastUpdated,
    refresh: () => fetchData({ force: true }) 
  };
}
